import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import {
  Appointment,
  DataType,
  FORMS_MODE,
  IFilter,
  IPatientPageParameter,
  IPatientUser,
  IWidgetPatientParameter,
  Notification,
  PatientWidgetName,
  PreferenceContext,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filter, first, skipWhile, takeUntil } from "rxjs/operators";

import { ConfirmationDialogComponent, ConfirmationDialogType, GlobalHelpDialogComponent } from "@comunicare/comunicare-dashboard-modals";
import { HelpData } from "src/app/helpers/helpData";
import { AppointmentService } from "src/app/providers/appointment.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { ResponsiveService } from "src/app/providers/responsive.service";
import { SessionService } from "src/app/providers/session.service";
import { TileManager } from "src/app/providers/tile-manager.service";
import { UserService } from "src/app/providers/user.service";
import { AppointmentDelayDialogComponent } from "../appointment-delay-dialog/appointment-delay-dialog.component";
import { WidgetBaseComponent } from "../base/widget-base/widget-base.component";
import { Item } from "../item-selector/item-selector.component";
import { PatientTeleconsultationDialogComponent } from "../patient-teleconsultations-dialog/patient-teleconsultations-dialog.component";
import { WidgetActionConfig } from "../widget-actions/widget-actions.component";
import { TeleconsultationsListDataSource } from "./teleconsultations-list-datasource";

@Component({
  selector: "app-patient-teleconsultations",
  templateUrl: "./patient-teleconsultations.component.html",
  styleUrls: ["./patient-teleconsultations.component.scss", "../base/widget-base/widget-base.component.scss"],
})
export class PatientTeleconsultationsComponent extends WidgetBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  public actions: WidgetActionConfig[] = [];
  @Input() set patientUser(pu: IPatientUser) {
    if (pu?.user?.caremateIdentifier) {
      this.pu = pu;
      this.loadPreferences();
      setTimeout(() => {
        this.initDataAndAutoRefresh(pu.user.caremateIdentifier, this.isBig ? this.allTeleconsultations : false);
      }, 500);
      if (this.sessionService.currentService && this.userService.ownOrganization) {
        this.setupServicesWatch();
      } else {
        this.sessionService.servicesSetupWatch
          .pipe(
            skipWhile(() => !this.userService.ownOrganization),
            first()
          )
          .subscribe(() => {
            this.setupServicesWatch();
          });
      }
    }
  }
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatTable) table: MatTable<Appointment>;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("focus") target: ElementRef;
  public scrollAfterDataInit = false;
  public displayedColumns: string[] = [
    "startDate",
    "endDate",
    "object",
    "participant",
    "dateSeenByPatient",
    "practitionerLastConnection",
    "patientLastConnection",
    "action",
  ];
  public visios: Appointment[] = [];
  public dataSource: TeleconsultationsListDataSource;
  /** Raw active filters, kept in sync to feed `app-item-filter` via the `filterFor` pipe. */
  public activeFilters: IFilter[] = [];
  public intakeDrugs: Notification[] = [];
  public sliderData: Item[] = [];
  public pu: IPatientUser;
  public defaultFilterAlreadyApply = false;
  public isBig = false;
  public allTeleconsultations = false;
  public isAllServices = true;
  public isSameOrganization = false;
  public isSameService = false;
  public newAppointmentTooltip: string;

  /**
   * IFilter
   */
  public dataTypeText = DataType.TEXT;
  public dataTypeDate = DataType.DATE;
  public dataTypeChoice = DataType.CHOICE;
  public globalSearchValue = "";
  public loading = true;
  public has2FA = false;
  public skip2FA = false;
  /** PUT appointment right, computed once instead of for each row of the table */
  public canUpdateAppointment = false;
  private refreshInterval;
  private serviceSub: Subscription;
  private serviceMonitSub: Subscription;
  private organizationSub: Subscription;
  /** Subject that emits when the component has been destroyed. */

  constructor(
    private appointmentService: AppointmentService,
    private preferenceService: PreferenceService,
    private translateService: TranslateService,
    private userService: UserService,
    private responsiveDialog: ResponsiveDialogService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    public sessionService: SessionService,
    public helpData: HelpData,
    private tileManager: TileManager,
    protected responsiveService: ResponsiveService
  ) {
    super(responsiveService);
    this.sessionService.refreshTeleconsultationsDataList.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.initDataAndAutoRefresh(this.pu.user.caremateIdentifier, this.isBig ? this.allTeleconsultations : false);
    });
    this.isBig = TileManager.isBig(PatientWidgetName.TELECONSULTATIONS);
  }

  ngOnInit(): void {
    this.canUpdateAppointment = this.userService.isAuthorizedSync(null, "appointment", "PUT");
    this.userService
      .account()
      .pipe(
        takeUntil(this.onDestroy$),
        filter((a) => a !== null && a !== undefined)
      )
      .subscribe((account) => {
        this.has2FA = account?.mfaActive;
      });
    this.dataSource = new TeleconsultationsListDataSource();
  }

  ngAfterViewInit(): void {
    this.skip2FA = this.sessionService.skip2FA ?? false;
    this.initPaginatorAndSort();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.clearAutoRefresh();
  }

  private setupServicesWatch() {
    // This is a special case because, when its a monitoring user, we cannot
    // allow creation of teleconsultation when EITHER the normal services OR the
    // monitoring services are in "all" mode
    this.setupServices();
    this.serviceSub?.unsubscribe();
    this.serviceSub = this.sessionService.refreshCurrentService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.setupServices();
    });
    if (this.userService.isMonitoringUser) {
      this.serviceMonitSub?.unsubscribe();
      this.serviceMonitSub = this.sessionService.refreshCurrentMonitService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.setupServices();
      });
    }
    this.organizationSub?.unsubscribe();
    this.organizationSub = this.sessionService.refreshOrganizationsList.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.setupServices();
    });
  }

  private setupServices() {
    if (this.sessionService.organization.reference !== this.pu?.patient?.managingOrganization?.reference) {
      this.isSameOrganization = false;
    } else if (this.sessionService.organization.reference === this.pu?.patient?.managingOrganization?.reference) {
      this.isSameOrganization = true;
    }

    if (
      this.pu.patient.healthcareservice.find((patientService) => patientService.reference === this.sessionService.currentService?.reference)
    ) {
      this.isSameService = true;
    } else {
      this.isSameService = false;
    }

    if (!this.sessionService.currentService || (this.userService.isMonitoringUser && !this.sessionService.currentMonitoringService)) {
      this.isAllServices = true;
      return;
    }
    if (this.userService.isMonitoringUser) {
      this.isAllServices = this.sessionService.isAllServices(true) || this.sessionService.isAllServices(false);
    } else {
      this.isAllServices = this.sessionService.isAllServices(false);
    }

    // Set tooltip for the "+" icon
    if (!this.isSameOrganization) {
      this.newAppointmentTooltip = this.translateService.instant("page.patient.notSameOrganizationError");
    } else if (this.isAllServices) {
      this.newAppointmentTooltip = this.translateService.instant("btn.disabledWithAll");
    } else if (!this.isSameService) {
      this.newAppointmentTooltip = this.translateService.instant("page.patient.notSameServiceError");
    } else {
      this.newAppointmentTooltip = "";
    }
  }

  private initDataAndAutoRefresh(userId: string, allTeleconsultations: boolean) {
    if (this.refreshInterval) {
      this.clearAutoRefresh();
    }
    this.refreshInterval = setInterval(() => {
      this.initData(userId, allTeleconsultations);
    }, 5 * 1000);
    this.initData(userId, allTeleconsultations);
  }

  private clearAutoRefresh() {
    clearInterval(this.refreshInterval);
  }

  private initData(userId: string, allTeleconsultations: boolean) {
    this.appointmentService
      .listVisioAppointements(userId, allTeleconsultations)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((obs) => {
        this.visios = obs;
        this.loadDataSource();
        if (!this.defaultFilterAlreadyApply) {
          this.sort?.sort({
            id: "startDate",
            start: "desc",
            disableClear: false,
          });
          this.defaultFilterAlreadyApply = true;
        }
        this.loading = false;
      });
  }

  public initPaginatorAndSort(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.table.dataSource = this.dataSource;
  }

  public loadDataSource(): void {
    this.dataSource.loadData(this.visios);
  }

  /**
   * IFilter
   */
  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }

  public applyFilter(filter: IFilter): void {
    this.dataSource.setFilter(filter);
    this.activeFilters = [...(this.dataSource?.getAllFilters() ?? [])];
  }

  public clearFilter(): void {
    this.globalSearchValue = "";
    this.dataSource.clearFilter();
    this.activeFilters = [];
  }

  /**
   * Preferences
   */
  public updatePreference(updateFocus: boolean): void {
    this.preferenceService
      .update({
        context: PreferenceContext.PATIENT_TELECONSULTATIONS_LIST,
        parameters: {
          isBig: this.isBig,
          allTeleconsultations: this.allTeleconsultations,
        } as IWidgetPatientParameter,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        if (updateFocus) {
          this.updateLastFocus();
        }
      });
  }

  private loadPreferences() {
    this.preferenceService
      .list(PreferenceContext.PATIENT_TELECONSULTATIONS_LIST)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((parameters: IWidgetPatientParameter) => {
        if (parameters) {
          this.allTeleconsultations = parameters.allTeleconsultations;
          this.isBig = this.sessionService.globalPref?.keepLayoutFromPatientPage
            ? parameters.isBig
            : TileManager.isBig(PatientWidgetName.TELECONSULTATIONS);
          this.tileManager.updateList(PatientWidgetName.TELECONSULTATIONS, this.isBig);
        }

        if (this.scrollAfterDataInit && this.sessionService.globalPref?.keepLayoutFromPatientPage) {
          this.scroll();
        }
      });
  }

  public canStartVideoCall(appointment: Appointment): boolean {
    return this.appointmentService.canStartVideoCall(appointment, this.canUpdateAppointment);
  }

  public isVideoCallDisabled(appointment: Appointment): boolean {
    return (!this.has2FA && !this.skip2FA) || !this.canStartVideoCall(appointment);
  }

  public videoCallTooltip(appointment: Appointment): string {
    if (!this.has2FA && !this.skip2FA) {
      return "mfa.need2FAForTeleconsultation";
    }
    return this.canStartVideoCall(appointment) ? "btn.startVideoCall" : "table.videoCallNotAssigned";
  }

  public onStartVideoCall(appointment: Appointment): void {
    if (!this.has2FA && !this.skip2FA) {
      return;
    }
    this.appointmentService.startVideoCall(appointment);
  }

  public onEditVisio(appointment: Appointment): void {
    this.dialog.open(PatientTeleconsultationDialogComponent, {
      data: {
        appointment,
        patientUser: this.pu,
        mode: FORMS_MODE.UPDATE,
        fromPatientWidget: true,
      },
    });
  }

  public onAddVisio(): void {
    this.dialog
      .open(PatientTeleconsultationDialogComponent, {
        data: {
          appointment: null,
          patientUser: this.pu,
          mode: FORMS_MODE.CREATE,
          fromPatientWidget: true,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.initDataAndAutoRefresh(this.pu.user.caremateIdentifier, this.isBig ? this.allTeleconsultations : false);
      });
  }

  public onDeleteVisio(appointment: Appointment): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          message: this.translateService.instant("common.removeForEverAsk"),
          type: ConfirmationDialogType.CONFIRM,
        },
      })
      .afterClosed()
      .subscribe((yes) => {
        if (yes) {
          this.appointmentService.deleteApp(appointment).subscribe(() => {
            this.initDataAndAutoRefresh(this.pu.user.caremateIdentifier, this.isBig ? this.allTeleconsultations : false);
            const msg = this.translateService.instant("common.success");
            this.snackBar.open(msg, "ok", { duration: 3000 });
          });
        }
      });
  }

  public openPTeleconsulationHelp(): void {
    this.responsiveDialog.open(
      GlobalHelpDialogComponent,
      {
        data: { slides: this.helpData.patientTeleconsultationsHelp },
        disableClose: true,
      },
      { maxWidth: "80vw" }
    );
  }

  /**
   * TODO : NONE BIG MODE YET
   */
  public updateLastFocus(): void {
    this.preferenceService
      .update({
        context: PreferenceContext.PATIENT_PAGE,
        parameters: {
          lastFocusWidgetName: PatientWidgetName.TELECONSULTATIONS,
        } as IPatientPageParameter,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  public scroll(): void {
    if (this.target) {
      setTimeout(() => {
        this.target.nativeElement.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }

  public onPlus(): void {
    this.isBig = !this.isBig;
    this.tileManager.updateList(PatientWidgetName.TELECONSULTATIONS, this.isBig);
    this.updatePreference(true);
    this.initDataAndAutoRefresh(this.pu.user.caremateIdentifier, this.isBig ? this.allTeleconsultations : false);
    this.scroll();
  }

  public refreshData(): void {
    this.updatePreference(false);
    this.initDataAndAutoRefresh(this.pu.user.caremateIdentifier, this.isBig ? this.allTeleconsultations : false);
  }

  public updateAppointmentWithDelay(appointment: Appointment): void {
    this.dialog
      .open(AppointmentDelayDialogComponent, {
        data: {
          appointment: appointment,
        },
      })
      .afterClosed()
      .subscribe((minutesDelay) => {
        if (typeof minutesDelay === "number") {
          appointment.minutesDelay = minutesDelay;
          this.appointmentService.updateVisioAppointement(appointment).subscribe(() => {
            this.snackBar.open(`✅ ${this.translateService.instant("common.success")}`, undefined, {
              duration: 3000,
            });
          });
        }
      });
  }
}
